<template>
    <b-modal id="add-product-modal" size="lg" hide-footer no-close-on-backdrop no-close-on-esc @hidden="closeEvent">
        <div v-if="showLoader"
            class="loader-wrapper d-flex justify-content-center align-items-center position-absolute z-index-100">
            <b-spinner style="width: 3rem; height: 3rem" variant="primary" />
        </div>

        <template #modal-title>Add Product</template>

        <b-container fluid>
            <b-row>
                <!-- Custom Product Toggle -->
                <b-col cols="12" class="mb-3">
                    <b-form-checkbox v-model="isCustomProduct" switch size="md">
                        Custom Product
                    </b-form-checkbox>
                </b-col>

                <!-- Custom Product Fields -->
                <template v-if="isCustomProduct">
                    <b-col md="6">
                        <b-form-group label="Product Name">
                            <b-form-input 
                                v-model="formData.customProductName" 
                                placeholder="Add Product Name"
                                :state="errors.customProductName ? false : null"
                            />
                            <div v-if="errors.customProductName" class="text-danger small mt-1">
                                {{ errors.customProductName }}
                            </div>
                        </b-form-group>
                    </b-col>

                    <b-col md="6">
                        <b-form-group label="Select Factory">
                            <v-select 
                                v-model="formData.selectedFactoryId" 
                                :options="factories" 
                                label="name"
                                :reduce="(factory) => factory.id" 
                                placeholder="Please select factory"
                                :clearable="false" 
                            />
                        </b-form-group>
                    </b-col>

                    <b-col md="6">
                        <b-form-group label="Design Name">
                            <b-form-input 
                                v-model="formData.customDesignName" 
                                placeholder="Add Design name"
                                :state="errors.customDesignName ? false : null"
                            />
                            <div v-if="errors.customDesignName" class="text-danger small mt-1">
                                {{ errors.customDesignName }}
                            </div>
                        </b-form-group>
                    </b-col>
                    
                    <!-- Assets Upload for Custom Product -->
                    <b-col cols="12">
                        <b-card title="Upload Files" class=" mt-3">
                            <b-form-group>
                                <input 
                                    type="file" 
                                    class="d-none" 
                                    multiple 
                                    id="assets" 
                                    ref="assetsInput"
                                    @change="(e) => fileChange(e, 'assets')" 
                                />
                                <label 
                                    for="assets" 
                                    class="btn  cursor-pointer"
                                    :class="errors.productAssets ? 'btn-outline-danger' : 'btn-outline-success'"
                                >
                                    <CIcon name="cilArrowThickFromBottom" class="mr-2" />
                                    Upload Assets
                                </label>
                                <div v-if="errors.productAssets" class="text-danger small mt-1">
                                    {{ errors.productAssets }}
                                </div>
                            </b-form-group>

                            <!-- Asset Previews -->
                            <div class="d-flex flex-wrap mt-2">
                                <div 
                                    v-for="(item, index) in formData.productAssets" 
                                    :key="index"
                                    class="position-relative mr-2 mb-2" 
                                    style="width: 100px; height: 100px"
                                >
                                    <b-button 
                                        variant="danger" 
                                        size="sm" 
                                        class="position-absolute"
                                        style="top: 5px; left: 5px; z-index: 1"
                                        @click="removeFile(index, 'assets')"
                                    >
                                        <CIcon name="cilTrash"  />
                                    </b-button>
                                    <img 
                                        :src="getFilePreview(item)" 
                                        class="w-100 h-100"
                                        style="object-fit: cover; border: 1px solid #dee2e6" 
                                        alt="Preview" 
                                    />
                                </div>
                            </div>
                        </b-card>
                    </b-col>
                </template>

                <!-- Standard Product Fields -->
                <template v-else>
                    <b-col md="6" class="">
                        <b-form-group label="Select Product/SKU">
                            <v-select 
                                v-model="formData.selectedProduct" 
                                :options="companyProducts"
                                label="display_name" 
                                placeholder="Please select product"
                                @input="onProductSelect" 
                            />
                            <div v-if="errors.selectedProduct" class="text-danger small mt-1">
                                {{ errors.selectedProduct }}
                            </div>
                        </b-form-group>
                    </b-col>

                    <b-col md="6" class="">
                        <b-form-group label="Select Factory">
                            <v-select 
                                v-model="formData.selectedFactoryId" 
                                :options="factories" 
                                label="name"
                                :reduce="(factory) => factory.id" 
                                placeholder="Please select factory"
                                :clearable="false" 
                            />
                        </b-form-group>
                    </b-col>

                    <b-col md="6" class="">
                        <b-form-group label="Select Style">
                            <v-select 
                                v-model="formData.selectedStyle"
                                :options="formData.selectedProduct ? formData.selectedProduct.productstyles : []"
                                label="name" 
                                placeholder="Select style" 
                                :disabled="!formData.selectedProduct"
                                @input="onStyleSelect" 
                            />
                            <div v-if="errors.selectedStyle" class="text-danger small mt-1">
                                {{ errors.selectedStyle }}
                            </div>
                        </b-form-group>
                    </b-col>

                    <b-col md="6" class="">
                        <b-form-group label="Select Design">
                            <v-select 
                                v-model="formData.selectedDesign"
                                :options="formData.selectedStyle ? formData.selectedStyle.productdesigns : []"
                                label="design_name" 
                                placeholder="Select design"
                                :disabled="!formData.selectedStyle" 
                            />
                            <div v-if="errors.selectedDesign" class="text-danger small mt-1">
                                {{ errors.selectedDesign }}
                            </div>
                        </b-form-group>
                    </b-col>
                </template>

                <!-- Front and Back Image Upload -->
                <b-col md="6" class="">
                    <b-form-group label="Upload Front Image">
                        <input 
                            type="file" 
                            class="d-none" 
                            id="frontimage" 
                            ref="frontImageInput" 
                            accept="image/*"
                            @change="(e) => fileChange(e, 'frontimage')" 
                        />
                        <label 
                            for="frontimage" 
                            class="btn pull-left cursor-pointer"
                            :class="errors.frontImage ? 'btn-outline-danger' : 'btn-outline-success'"
                        >
                            <CIcon name="cilArrowThickFromBottom" class="mr-2" />
                            Upload Front Image
                        </label>
                        <div v-if="errors.frontImage" class="text-danger small mt-1">
                            {{ errors.frontImage }}
                        </div>

                        <div 
                            v-if="formData.frontImage" 
                            class="position-relative mt-2"
                            style="width: 100px; height: 100px"
                        >
                            <b-button 
                                variant="danger" 
                                size="sm" 
                                class="position-absolute"
                                style="top: 5px; left: 5px; z-index: 1" 
                                @click="removeFile(null, 'frontimage')"
                            >
                                <CIcon name="cilTrash" />
                            </b-button>
                            <img 
                                :src="getFilePreview(formData.frontImage)" 
                                class="w-100 h-100"
                                style="object-fit: cover; border: 1px solid #dee2e6" 
                                alt="Preview" 
                            />
                        </div>
                    </b-form-group>
                </b-col>

                <b-col md="6" class="">
                    <b-form-group label="Upload Back Image">
                        <input 
                            type="file" 
                            class="d-none" 
                            id="backimage" 
                            ref="backImageInput" 
                            accept="image/*"
                            @change="(e) => fileChange(e, 'backimage')" 
                        />
                        <label 
                            for="backimage" 
                            class="btn pull-right cursor-pointer"
                            :class="errors.backImage ? 'btn-outline-danger' : 'btn-outline-success'"
                        >
                            <CIcon name="cilArrowThickFromBottom" class="mr-2" />
                            Upload Back Image
                        </label>
                        <div v-if="errors.backImage" class="text-danger small mt-1">
                            {{ errors.backImage }}
                        </div>

                        <div 
                            v-if="formData.backImage" 
                            class="position-relative mt-2"
                            style="width: 100px; height: 100px"
                        >
                            <b-button 
                                variant="danger" 
                                size="sm" 
                                class="position-absolute"
                                style="top: 5px; left: 5px; z-index: 1" 
                                @click="removeFile(null, 'backimage')"
                            >
                                <CIcon name="cilTrash" />
                            </b-button>
                            <img 
                                :src="getFilePreview(formData.backImage)" 
                                class="w-100 h-100"
                                style="object-fit: cover; border: 1px solid #dee2e6" 
                                alt="Preview" 
                            />
                        </div>
                    </b-form-group>
                </b-col>

                <!-- Addons Section -->
                <b-col cols="12" v-if="hasAddons">
                    <b-card title="Select Addons">
                        
                        <!-- Grouped Addons -->
                        <template v-if="hasGroupedAddons">
                        <div 
                            v-for="(group, groupName) in formData.selectedStyle.customized_addons.grouped_addons"
                            :key="groupName" 
                            class="mb-3"
                        >
                            <h6>{{ groupName }}:</h6>
                            <b-form-radio-group
                            v-model="selectedGroupAddon[groupName]"
                            :name="`group-${groupName}`"
                            @change="addon => setSelectedGroupedAddons(groupName, addon)"
                            class="d-flex flex-wrap"
                            >
                            <b-form-radio
                                v-for="addon in group"
                                :key="addon.id"
                                :value="addon"
                                class="mr-2 mb-2 d-flex align-items-center"
                            >
                                <i 
                                class="fa fa-check mr-1"
                                v-if="selectedGroupAddon[groupName] && selectedGroupAddon[groupName].id === addon.id"
                                ></i>
                                {{ addon.sku_id }}
                            </b-form-radio>
                            </b-form-radio-group>
                        </div>
                        <hr />
                        </template>

                        <!-- Ungrouped Addons -->
                        <template v-if="hasUngroupedAddons">
                        <b-form-checkbox-group class="d-flex flex-wrap ">
                            <b-form-checkbox
                            v-for="addon in formData.selectedStyle.customized_addons.ungrouped_addons"
                            :key="addon.id"
                            v-model="addon.is_selected"
                            class="mr-2 mb-2 d-flex align-items-center"
                            >
                            <i class="fa fa-check mr-1" v-if="addon.is_selected"></i>
                            {{ addon.sku_id }}
                            </b-form-checkbox>
                        </b-form-checkbox-group>
                        <hr />
                        </template>

                        <!-- Active Addons -->
                        <template v-if="hasActiveAddons">
                        <b-form-checkbox-group class="d-flex flex-wrap">
                            <b-form-checkbox
                            v-for="(addon, addonIndex) in formData.selectedProduct.active_addons"
                            :key="addon.id"
                            :value="addon"
                            v-model="selectedAddons"
                            @change="(checked) => setSelectedAddons({ target: { checked } }, addon, addonIndex)"
                            class="mr-2 mb-2 d-flex align-items-center"
                            >
                            <i class="fa fa-check mr-1" v-if="isAddonSelected(addon)"></i>
                            {{ addon.title }}
                            </b-form-checkbox>
                        </b-form-checkbox-group>
                        <hr />
                        </template>

                        <!-- Logo Technologies -->
                        <template v-if="hasLogoTechnologies">
                        <b-form-checkbox-group class="d-flex flex-wrap">
                            <b-form-checkbox
                            v-for="(tech, techIndex) in formData.selectedProduct.logo_technologies"
                            :key="tech.sku_id"
                            :value="tech"
                            :checked="isTechnologySelected(tech)"
                            @change="(checked) => setLogoTechnologies({ target: { checked } }, tech, techIndex)"
                            button
                            variant="outline-primary"
                            class="mr-2 mb-2 d-flex align-items-center"
                            >
                            <i class="fa fa-check mr-1" v-if="isTechnologySelected(tech)"></i>
                            {{ tech.label }}
                            </b-form-checkbox>
                        </b-form-checkbox-group>
                        </template>

                    </b-card>
                </b-col>


                <!-- Roster Section -->
                <b-col cols="12">
                    <b-card title="Order Details" class="">
                       <b-row class="mb-3">
                            <b-col cols="12" md="6" class="mb-2 mb-md-0">
                                <b-button 
                                variant="info" 
                                :disabled="!formData.selectedProduct"
                                @click="downloadTemplateHandler"
                                class="w-100"
                                >
                                <CIcon name="cilCloudDownload" />
                                Download Excel Template
                                </b-button>
                            </b-col>
                            <b-col cols="12" md="6">
                                <input 
                                type="file" 
                                class="d-none" 
                                id="rosterinput" 
                                ref="rosterInput"
                                @change="(e) => fileChange(e, 'roster')" 
                                />
                                <b-button
                                variant="success"
                                class="w-100"
                                @click="$refs.rosterInput.click()"
                                >
                                <CIcon name="cilArrowThickFromBottom" />
                                Upload Excel Template
                                </b-button>
                            </b-col>
                        </b-row>`

                        <b-row class="mb-3">
                            <b-col md="6">
                                <b-form-group label="Select Locker">
                                    <v-select 
                                    v-model="selectedLocker" 
                                    :options="lockerRoom" 
                                    label="room_name"
                                    placeholder="Select Locker"
                                    :clearable="true"
                                    />
                                </b-form-group>
                            </b-col>
                            <b-col md="6">
                                <b-form-group label="Select Locker Product">
                                    <v-select 
                                    v-model="selectedLockerProduct"
                                    :options="availableLockerProducts"
                                    label="product_name" 
                                    placeholder="Select Locker Product"
                                    :disabled="availableLockerProducts.length === 0"
                                    @input="setRosterData" 
                                    />
                                </b-form-group>
                                </b-col>
                        </b-row>

                        <!-- Players Table -->
                        <b-table-simple responsive>
                            <b-thead>
                                <b-tr>
                                    <b-th>Name</b-th>
                                    <b-th>No</b-th>
                                    <b-th>Size</b-th>
                                    <b-th>Qty</b-th>
                                    <b-th></b-th>
                                </b-tr>
                            </b-thead>
                            <b-tbody>
                                <b-tr v-for="(player, index) in formData.players" :key="index">
                                    <b-td>
                                        <b-form-input 
                                            v-model="player.text" 
                                            :state="getPlayerError(index, 'text') ? false : null"
                                            :title="getPlayerError(index, 'text')"
                                        />
                                    </b-td>
                                    <b-td>
                                        <b-form-input 
                                            v-model="player.number" 
                                            :state="getPlayerError(index, 'number') ? false : null"
                                            :title="getPlayerError(index, 'number')"
                                            style="width: 80px" 
                                        />
                                    </b-td>
                                    <b-td>
                                        <div v-if="isCustomProduct">
                                            <b-form-input 
                                                v-model="player.size" 
                                                :state="getPlayerError(index, 'size') ? false : null"
                                                :title="getPlayerError(index, 'size')"
                                            />
                                        </div>
                                        <div v-else class="d-flex align-items-center">
                                            <i 
                                                v-if="getPlayerError(index, 'size')"
                                                class="fa fa-exclamation-circle text-danger mr-2"
                                                :title="getPlayerError(index, 'size')"
                                            ></i>
                                            <v-select 
                                                v-model="player.size" 
                                                :options="productSizes"
                                                :clearable="false" 
                                                style="width: 100%"
                                                append-to-body
                                            />
                                        </div>
                                    </b-td>
                                    <b-td>
                                        <b-form-input 
                                            v-model.number="player.quantity" 
                                            type="number" 
                                            min="1"
                                            :state="getPlayerError(index, 'quantity') ? false : null"
                                            :title="getPlayerError(index, 'quantity')"
                                            style="width: 80px" 
                                        />
                                    </b-td>
                                    <b-td>
                                        <div v-if="index > 0" class="delete-icon" @click="removePlayer(index)">
                                            <Delete :size="20" fillColor="#777" />
                                         </div>
                                    </b-td>
                                </b-tr>
                            </b-tbody>
                        </b-table-simple>

                        <b-button variant="info" @click="addPlayer" class="mt-2">
                            Add Player
                        </b-button>
                        <b-button 
                            v-if="canCopyRoster" 
                            variant="success" 
                            @click="copyRoster" 
                            class="mt-2 ml-2"
                        >
                            Copy Roster
                        </b-button>
                        <b-button 
                            v-if="canPasteRoster" 
                            variant="info" 
                            @click="pasteRoster" 
                            class="mt-2 ml-2"
                        >
                            Paste Roster
                        </b-button>
                    </b-card>
                </b-col>

                <!-- Action Buttons -->
                <b-col cols="12" class="text-right">
                    <b-button variant="secondary" @click="closeEvent" class="mr-2">
                        Close
                    </b-button>
                    <b-button variant="success" :disabled="showLoader" @click="onSubmit">
                        Add Product
                    </b-button>
                </b-col>
            </b-row>
        </b-container>
    </b-modal>
</template>

<script>
import Vue from 'vue';
import { http } from '@/config/httpCommon';
import { handleResponseException } from '@/helpers/Helpers';
import readXlsxFile from 'read-excel-file';
import { CIcon } from '@coreui/icons-vue';





export default Vue.extend({
    name: 'AddProductModal',
    components: {
    CIcon,        
    },
    props: {
        customerId: {
            type: Number,
            required: true,
        },
        companyId: {
            type: Number,
            required: true,
        },
        orderItem: {
            type: Object,
            required: true,
        },
        lockerRoom: {
            type: Array,
            default: () => [],
        },
        factories: {
            type: Array,
            default: () => [],
        },
        companyProducts: {
            type: Array,
            default: () => [],
        },
        copiedRoster: {
            type: Object,
            default: () => ({
                isAnyRosterCopied: false,
                copiedFromProductIndex: null,
                rosterData: [],
            }),
        },
    },
    data() {
        return {
            showLoader: false,
            isCustomProduct: false,
            errors: {},
            formData: {
                selectedProduct: null,
                selectedStyle: null,
                selectedDesign: null,
                customProductName: null,
                customDesignName: null,
                productAssets: [],
                frontImage: null,
                backImage: null,
                selectedFactoryId: null,
                players: [
                    {
                        text: '',
                        number: '',
                        size: '',
                        quantity: 1,
                        information: '',
                    },
                ],
            },
            
            playerErrors: [],
            selectedAddons: [],
            selectedTechnology: [],
            selectedGroupAddon: {},
            selectedLocker: null,
            selectedLockerProduct: null,
        };
    },    
    computed: {
        productSizes() {
            if (this.formData.selectedProduct && this.formData.selectedProduct.sizes) {
                return this.formData.selectedProduct.sizes[0].json_data.map((size) => size.name);
            }
            return [];
        },
        // Add this new computed property
        availableLockerProducts() {
            if (!this.selectedLocker || !this.selectedLocker.products) {
            return [];
            }
            return this.selectedLocker.products;
        },
        hasAddons() {
            return this.hasGroupedAddons || this.hasUngroupedAddons || this.hasActiveAddons || this.hasLogoTechnologies;
        },
        hasGroupedAddons() {
            return (
                this.formData.selectedStyle &&
                this.formData.selectedStyle.customized_addons &&
                this.formData.selectedStyle.customized_addons.grouped_addons &&
                Object.keys(this.formData.selectedStyle.customized_addons.grouped_addons).length > 0
            );
        },
        hasUngroupedAddons() {
            return (
                this.formData.selectedStyle &&
                this.formData.selectedStyle.customized_addons &&
                this.formData.selectedStyle.customized_addons.ungrouped_addons &&
                this.formData.selectedStyle.customized_addons.ungrouped_addons.length > 0
            );
        },
        hasActiveAddons() {
            return (
                this.formData.selectedProduct &&
                this.formData.selectedProduct.active_addons &&
                this.formData.selectedProduct.active_addons.length > 0
            );
        },
        hasLogoTechnologies() {
            return (
                this.formData.selectedProduct &&
                this.formData.selectedProduct.logo_technologies &&
                this.formData.selectedProduct.logo_technologies.length > 0
            );
        },
        canCopyRoster() {
            return (
                ((this.isCustomProduct && this.formData.customProductName) || this.formData.selectedProduct) &&
                this.formData.players.length > 0
            );
        },
        canPasteRoster() {
            const productId = this.isCustomProduct ? this.formData.customProductName : this.formData.selectedProduct?.id;
            return (
                ((this.isCustomProduct && this.formData.customProductName) || this.formData.selectedProduct) &&
                this.copiedRoster.isAnyRosterCopied &&
                this.copiedRoster.copiedFromProductIndex !== productId
            );
        },
    },
    watch: {
        isCustomProduct(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.handleProductTypeChange();
            }
        },
        'formData.selectedProduct'(newProduct) {
            if (newProduct) {
                this.formData.selectedStyle = null;
                this.formData.selectedDesign = null;
            }
        },
        'formData.selectedStyle'(newStyle) {
            if (newStyle) {
                this.formData.selectedDesign = null;
            }
        },
    },
    methods: {
        async validateForm() {
            try {
                // Initialize errors as reactive objects
                this.errors = {};
                this.playerErrors = [];

                let hasErrors = false;

                // Validate based on product type
                if (this.isCustomProduct) {
                    if (!this.formData.customProductName || this.formData.customProductName.trim() === '') {
                        this.$set(this.errors, 'customProductName', 'Product name is required');
                        hasErrors = true;
                    }
                    if (!this.formData.customDesignName || this.formData.customDesignName.trim() === '') {
                        this.$set(this.errors, 'customDesignName', 'Design name is required');
                        hasErrors = true;
                    }
                    if (!this.formData.productAssets || this.formData.productAssets.length === 0) {
                        this.$set(this.errors, 'productAssets', 'At least one asset is required');
                        hasErrors = true;
                    }
                } else {
                    if (!this.formData.selectedProduct) {
                        this.$set(this.errors, 'selectedProduct', 'Product selection is required');
                        hasErrors = true;
                    }
                    if (this.formData.selectedProduct && !this.formData.selectedStyle) {
                        this.$set(this.errors, 'selectedStyle', 'Style selection is required');
                        hasErrors = true;
                    }
                    if (this.formData.selectedStyle && !this.formData.selectedDesign) {
                        this.$set(this.errors, 'selectedDesign', 'Design selection is required');
                        hasErrors = true;
                    }
                }

                // Validate front and back images (optional but show if you want them required)
                // if (!this.formData.frontImage) {
                //     this.$set(this.errors, 'frontImage', 'Front image is required');
                //     hasErrors = true;
                // }
                // if (!this.formData.backImage) {
                //     this.$set(this.errors, 'backImage', 'Back image is required');
                //     hasErrors = true;
                // }

                // Validate players - IMPORTANT: Initialize each player's error object
                this.formData.players.forEach((player, index) => {
                    // Initialize error object for this player
                    this.$set(this.playerErrors, index, {});

                    // Validate text field (optional - uncomment if required)
                    // if (!player.text || player.text.trim() === '') {
                    //     this.$set(this.playerErrors[index], 'text', 'Name is required');
                    //     hasErrors = true;
                    // }

                    // Validate number field
                    if (player.number && player.number.trim() !== '') {
                        if (!/^\d+$/.test(player.number)) {
                            this.$set(this.playerErrors[index], 'number', 'Number must contain only digits (0-9)');
                            hasErrors = true;
                        } else if (player.number.includes('.')) {
                            this.$set(this.playerErrors[index], 'number', 'Number must not contain decimals');
                            hasErrors = true;
                        }
                    }

                    // Enhanced size validation
                    let sizeIsEmpty = false;

                    if (player.size === null || player.size === undefined) {
                        sizeIsEmpty = true;
                    } else if (typeof player.size === 'string') {
                        sizeIsEmpty = player.size.trim() === '';
                    } else if (typeof player.size === 'object') {
                        sizeIsEmpty = Object.keys(player.size).length === 0;
                    }

                    if (sizeIsEmpty) {
                        this.$set(this.playerErrors[index], 'size', 'Size cannot be empty');
                        hasErrors = true;
                    }

                    // Validate quantity
                    if (!player.quantity || player.quantity < 1) {
                        this.$set(this.playerErrors[index], 'quantity', 'Quantity must be greater than 0');
                        hasErrors = true;
                    }
                });

                // Force reactivity update
                this.$forceUpdate();

                return !hasErrors;
            } catch (error) {
                console.error('Validation error:', error);
                return false;
            }
        },

        getPlayerError(index, field) {
            if (!this.playerErrors[index]) return '';
            const error = this.playerErrors[index][field] || '';

            // Debug logging
            if (field === 'size' && this.formData.players[index]) {
                if (error) {
                    console.log(`Player ${index} size error:`, error);
                }
            }

            return error;
        },

        async onSubmit() {
            this.showLoader = true;

            try {
                const isValid = await this.validateForm();
                
                if (!isValid) {
                    this.showToast('Please fix the validation errors before submitting', 'error');
                    this.showLoader = false;
                    return;
                }

                // Build the payload matching Vue3 structure
                const payload = this.buildAddProductPayload();
                
                // Step 1: Upload files first
                await this.uploadFactoryProductFiles(payload);

                // Step 2: Send to NEW backend endpoint (not the old update prices endpoint)
                const response = await http.post(
                    `order/${this.orderItem.order.id}/add-product`, 
                    payload
                );

                //this.showToast('Product added successfully', 'SUCCESS');
                
                // Emit event to refresh parent data
                this.$emit('product-added', response.data.result || response.data);
                this.closeEvent();
            } catch (error) {                
                handleResponseException(error);
                this.showToast('An error occurred while adding the product', 'error');
            } finally {
                this.showLoader = false;
            }
        },

        // Add this new method to build the payload matching Vue3
        buildAddProductPayload() {
            const selectedFactory = this.factories.find((fac) => fac.id === this.formData.selectedFactoryId);

            // This matches the Vue3 payload structure exactly
            const payload = {
                company_id: this.companyId,
                company_name: this.orderItem.order.company_name,
                customer_id: this.orderItem.order.customer_id,
                customer_name: this.orderItem.order.customer_name,
                factory_id: selectedFactory?.id || null,
                factory_name: selectedFactory?.name || null,
                factory_products: [],
            };

            const addons = this.getValidAddons();
            
            const factoryProductData = {
                id: '', // Will be generated on backend
                addons: addons,
                assets: this.isCustomProduct ? this.formData.productAssets : [],
                svg_url: '',
                sync_id: null,
                style_id: this.isCustomProduct ? null : this.formData.selectedStyle?.id,
                style_name: this.isCustomProduct ? null : this.formData.selectedStyle?.name,
                design_id: this.isCustomProduct ? -1 : this.formData.selectedDesign?.id,
                design_nick_name: this.isCustomProduct ? this.formData.customDesignName : null,
                svg_parts: [],
                back_image: 'placeholders/custom-design.png',
                front_image: 'placeholders/custom-design.png',
                is_new_product: this.isCustomProduct,
                new_product_name: this.isCustomProduct ? this.formData.customProductName : null,
                product_id: !this.isCustomProduct ? this.formData.selectedProduct?.id : null,
                sku_number: !this.isCustomProduct ? this.formData.selectedProduct?.sku?.sku_number : null,
                product_name: !this.isCustomProduct ? this.formData.selectedProduct?.display_name : null,
                product_type: !this.isCustomProduct ? this.formData.selectedProduct?.product_type : null,
                sort_order: null,
                reorder_data: null,
                grouped_addons: [],
                colors: [],
                svg_groups: [],
                groupcolors: [],
                logo_colors: [],
                defaultcolors: [],
                custom_logo_svgs: [],
                group_patterns: [],
                prices: null,
                production_url: '',
                back_image_short: '',
                fixed_logo_index: 0,
                ungrouped_addons: [],
                front_image_short: '',
                is_custom_product: this.isCustomProduct,
                custom_product_placeholder: 'placeholders/custom-design.png',
                measurement_ratio: !this.isCustomProduct ? this.formData.selectedProduct?.measurement_ratio : null,
                sizechart_reference: !this.isCustomProduct ? this.formData.selectedProduct?.sku?.sizechart_reference : null,
                shuffle_color_number: 1,
                product_roster_detail: this.formData.players,
                size_variants_mapping: null,
                minimum_order_quantity: !this.isCustomProduct ? this.formData.selectedProduct?.sku?.minimum_order_quantity : null,
                minimum_order_quantity_type: !this.isCustomProduct ? this.formData.selectedProduct?.sku?.minimum_order_quantity_type : null,
                product_custom_text_objects: { common: [], roster: [] },
                front_image_file: this.formData.frontImage,
                back_image_file: this.formData.backImage,
                ecommerce_cart_id: null,
                ecommerce_post_id: null,
                ecommerce_variant_id: null,
                ecommerce_modifier_id: null,
                product_custom_texts: [],
                custom_logos: [],
            };

            // Calculate prices
            const productPrices = this.getProductPrices(factoryProductData, this.formData.selectedProduct);
            factoryProductData.prices = productPrices;

            payload.factory_products.push(factoryProductData);
            
            return payload;
        },

        // Update the file upload method to work with the new payload structure
        async uploadFactoryProductFiles(payload) {
            const basePath = `company_${this.companyId}/${this.companyId}/order/${this.orderItem.order.customer_id}/${this.orderItem.order.id}`;
            const filePath = payload.factory_id ? `${basePath}/${payload.factory_id}` : `${basePath}/shareable`;

            for (const factoryProduct of payload.factory_products) {
                const formData = new FormData();
                let hasFiles = false;

                if (factoryProduct.front_image_file instanceof File) {
                    formData.append('front_image', factoryProduct.front_image_file);
                    hasFiles = true;
                }
                if (factoryProduct.back_image_file instanceof File) {
                    formData.append('back_image', factoryProduct.back_image_file);
                    hasFiles = true;
                }
                if (Array.isArray(factoryProduct.assets)) {
                    factoryProduct.assets.forEach((asset, index) => {
                        if (asset instanceof File) {
                            formData.append(`assets[${index}]`, asset);
                            hasFiles = true;
                        }
                    });
                }

                if (hasFiles) {
                    formData.append('base_path', filePath);
                    const response = await http.post('upload/price/modal/files', formData, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                    });

                    factoryProduct.front_image = response.data.result.front_image;
                    factoryProduct.back_image = response.data.result.back_image;
                    factoryProduct.assets = response.data.result.assets;

                    delete factoryProduct.front_image_file;
                    delete factoryProduct.back_image_file;
                }
            }
        },        

        getPayload() {
            const selectedFactory = this.factories.find((fac) => fac.id === this.formData.selectedFactoryId);

            const payload = {
                company_id: this.companyId,
                company_name: this.orderItem.order.company_name,
                customer_id: this.orderItem.order.customer_id,
                customer_name: this.orderItem.order.customer_name,
                factory_id: selectedFactory?.id || null,
                factory_name: selectedFactory?.name || null,
                factory_products: [],
                price_info: {},
            };

            const addons = this.getValidAddons();
            const factoryProductData = {
                id: '',
                addons: addons,
                assets: this.isCustomProduct ? this.formData.productAssets : [],
                svg_url: '',
                sync_id: null,
                style_id: this.isCustomProduct ? null : this.formData.selectedStyle?.id,
                style_name: this.isCustomProduct ? null : this.formData.selectedStyle?.name,
                design_id: this.isCustomProduct ? -1 : this.formData.selectedDesign?.id,
                design_nick_name: this.isCustomProduct ? this.formData.customDesignName : null,
                svg_parts: [],
                back_image: 'placeholders/custom-design.svg',
                front_image: 'placeholders/custom-design.svg',
                is_new_product: this.isCustomProduct,
                new_product_name: this.isCustomProduct ? this.formData.customProductName : null,
                product_id: !this.isCustomProduct ? this.formData.selectedProduct?.id : null,
                sku_number: !this.isCustomProduct ? this.formData.selectedProduct?.sku?.sku_number : null,
                product_name: !this.isCustomProduct ? this.formData.selectedProduct?.display_name : null,
                product_type: !this.isCustomProduct ? this.formData.selectedProduct?.product_type : null,
                sort_order: null,
                reorder_data: null,
                grouped_addons: [],
                colors: [],
                svg_groups: [],
                groupcolors: [],
                logo_colors: [],
                defaultcolors: [],
                custom_logo_svgs: [],
                group_patterns: [],
                prices: null,
                production_url: '',
                back_image_short: '',
                fixed_logo_index: 0,
                ungrouped_addons: [],
                front_image_short: '',
                is_custom_product: this.isCustomProduct,
                custom_product_placeholder: 'placeholders/custom-design.svg',
                measurement_ratio: !this.isCustomProduct ? this.formData.selectedProduct?.measurement_ratio : null,
                sizechart_reference: !this.isCustomProduct ? this.formData.selectedProduct?.sku?.sizechart_reference : null,
                shuffle_color_number: 1,
                product_roster_detail: this.formData.players,
                size_variants_mapping: null,
                minimum_order_quantity: !this.isCustomProduct ? this.formData.selectedProduct?.sku?.minimum_order_quantity : null,
                minimum_order_quantity_type: !this.isCustomProduct ? this.formData.selectedProduct?.sku?.minimum_order_quantity_type : null,
                product_custom_text_objects: { common: [], roster: [] },
                front_image_file: this.formData.frontImage,
                back_image_file: this.formData.backImage,
                ecommerce_cart_id: null,
                ecommerce_post_id: null,
                ecommerce_variant_id: null,
                ecommerce_modifier_id: null,
                product_custom_texts: [],
                custom_logos: [],
            };

            const productPrices = this.getProductPrices(factoryProductData, this.formData.selectedProduct);
            factoryProductData.prices = productPrices;

            payload.price_info = {
                discount: 0,
                is_fixed: false,
                shipping: 0,
                fixed_value: 0,
                total_price: productPrices.factory_product_total_price ?? 0,
                total_quantity: productPrices.total_quantity ?? 0,
                order_item_final_price: productPrices.factory_product_total_price ?? 0,
            };

            payload.factory_products.push(factoryProductData);
            return payload;
        },

        getProductPrices(factoryProduct, product) {
            let productSkuPrice = {
                is_fixed: false,
                fixed_value: 0,
                discount_applied: 0,
                price_applied_of: 'sku',
                discount_applied_in_percent: 0,
                net_price: 0,
                final_net_price: 0,
            };

            if (product) {
                productSkuPrice = product.sku.price;
            }

            let totalQuantity = 0;
            const productPrices = {
                sizes: [],
                addons: [],
                logo_technologies: [],
                grouped_addons: {},
                ungrouped_addons: [],
                total_addons: 0,
                updated_by_info: {
                    id: '',
                    name: '',
                    updated_at: '',
                },
                total_quantity: 0,
                sizes_discount_info: {
                    is_fixed: productSkuPrice.is_fixed,
                    fixed_value: productSkuPrice.fixed_value,
                    discount_applied: productSkuPrice.discount_applied,
                    price_applied_of: productSkuPrice.price_applied_of,
                    discount_applied_in_percent: productSkuPrice.discount_applied_in_percent,
                },
                factory_product_total_price: 0,
            };

            // Process sizes from roster
            factoryProduct.product_roster_detail.forEach((rosterData) => {
                totalQuantity += parseInt(rosterData.quantity);
                const productNetPrice = product ? parseFloat(product.sku.price.net_price) : 0;
                const productFinalNetPrice = product ? parseFloat(product.sku.price.final_net_price) : 0;
                const sizeQuantity = parseInt(rosterData.quantity);

                const sizeObj = {
                    size: rosterData.size,
                    quantity: sizeQuantity,
                    original_price: productNetPrice,
                    final_net_price: productFinalNetPrice,
                    final_net_price_with_quantity: productFinalNetPrice * sizeQuantity,
                };

                productPrices.sizes.push(sizeObj);
                productPrices.factory_product_total_price += sizeObj.final_net_price_with_quantity;
            });

            productPrices.total_quantity = totalQuantity;

            // Process regular addons
            productPrices.addons = this.selectedAddons.map((addon) => {
                const addonObj = {
                    addon_id: addon.addon_id,
                    is_fixed: addon.price_info.is_fixed,
                    net_price: parseFloat(addon.price_info.net_price),
                    addon_name: addon.title,
                    fixed_value: addon.price_info.fixed_value,
                    original_price: parseFloat(addon.price_info.net_price),
                    final_net_price: addon.price_info.final_net_price,
                    discount_applied: addon.price_info.discount_applied,
                    price_applied_of: addon.price_info.price_applied_of,
                    discount_applied_in_percent: 0,
                    final_net_price_with_quantity: addon.price_info.net_price ? parseFloat(addon.price_info.net_price) * totalQuantity : 0,
                };
                productPrices.factory_product_total_price += addonObj.final_net_price_with_quantity;
                return addonObj;
            });

            // Process logo technologies
            productPrices.logo_technologies = this.selectedTechnology.map((tech) => {
                const techObj = {
                    logo_id: '',
                    side: '',
                    logo_url: '',
                    addon_id: tech.sku_id,
                    is_fixed: tech.price_info.is_fixed,
                    net_price: parseFloat(tech.price_info.net_price),
                    addon_name: tech.label,
                    fixed_value: tech.price_info.fixed_value,
                    original_price: parseFloat(tech.price_info.net_price),
                    final_net_price: tech.price_info.final_net_price,
                    discount_applied: tech.price_info.discount_applied,
                    price_applied_of: tech.price_info.price_applied_of,
                    discount_applied_in_percent: 0,
                    final_net_price_with_quantity: tech.price_info.net_price ? parseFloat(tech.price_info.net_price) * totalQuantity : 0,
                };
                productPrices.factory_product_total_price += techObj.final_net_price_with_quantity;
                return techObj;
            });

            // Process grouped addons
            if (Object.keys(this.selectedGroupAddon).length > 0) {
                Object.entries(this.selectedGroupAddon).forEach(([group, addon]) => {
                    const priceInfo = addon.price_info;
                    const netPrice = priceInfo?.net_price ? parseFloat(priceInfo.net_price) : 0;
                    const finalNetPriceWithQuantity = netPrice * totalQuantity;

                    productPrices.grouped_addons[group] = {
                        addon_id: addon.id,
                        is_fixed: priceInfo?.is_fixed || false,
                        net_price: netPrice,
                        addon_name: addon.sku_id,
                        fixed_value: priceInfo?.fixed_value || 0,
                        original_price: netPrice,
                        final_net_price: priceInfo?.final_net_price || 0,
                        discount_applied: priceInfo?.discount_applied || 0,
                        price_applied_of: priceInfo?.price_applied_of || 'sku',
                        discount_applied_in_percent: priceInfo?.discount_applied_in_percent || 0,
                        final_net_price_with_quantity: finalNetPriceWithQuantity,
                    };

                    productPrices.factory_product_total_price += finalNetPriceWithQuantity;
                });
            }

            // Process ungrouped addons
            if (this.formData.selectedStyle && this.formData.selectedStyle.customized_addons) {
                productPrices.ungrouped_addons = this.formData.selectedStyle.customized_addons.ungrouped_addons
                    .filter((addon) => addon.is_selected)
                    .map((addon) => {
                        const ungroupedAddonObj = {
                            addon_id: addon.addon_id,
                            is_fixed: addon.price_info.is_fixed,
                            net_price: addon.price_info.net_price ? parseFloat(addon.price_info.net_price) : 0,
                            addon_name: addon.sku_id,
                            fixed_value: addon.price_info.fixed_value,
                            original_price: addon.price_info.net_price ? parseFloat(addon.price_info.net_price) : 0,
                            final_net_price: addon.price_info.final_net_price,
                            discount_applied: addon.price_info.discount_applied,
                            price_applied_of: addon.price_info.price_applied_of,
                            discount_applied_in_percent: addon.price_info.discount_applied_in_percent,
                            final_net_price_with_quantity: addon.price_info.net_price ? parseFloat(addon.price_info.net_price) * totalQuantity : 0,
                        };
                        productPrices.factory_product_total_price += ungroupedAddonObj.final_net_price_with_quantity;
                        return ungroupedAddonObj;
                    });
            }

            productPrices.total_addons =
                productPrices.addons.length +
                productPrices.ungrouped_addons.length +
                productPrices.logo_technologies.length +
                Object.keys(productPrices.grouped_addons).length;

            return productPrices;
        },

        getValidAddons() {
            return this.selectedAddons.map((addon) => {
            if (addon && typeof addon === 'object' && 'title' in addon) {
                return { ...addon, selected: true }; // Ensure selected is true
            } else if (typeof addon === 'string') {
                return { title: addon, selected: true };
            }
            return null;
            }).filter((addon) => addon !== null);
        },

        handleProductTypeChange() {
            this.formData.customProductName = null;
            this.formData.customDesignName = null;
            this.formData.productAssets = [];
            this.formData.frontImage = null;
            this.formData.backImage = null;
            this.formData.selectedProduct = null;
            this.formData.selectedStyle = null;
            this.formData.selectedDesign = null;
            this.formData.selectedFactoryId = null;

            // Clear errors
            this.errors = {};
            this.playerErrors = [];

            // Reset addon selections
            this.selectedAddons = [];
            this.selectedTechnology = [];
            this.selectedGroupAddon = {};

            // Reset file inputs
            if (this.$refs.assetsInput) this.$refs.assetsInput.value = '';
            if (this.$refs.frontImageInput) this.$refs.frontImageInput.value = '';
            if (this.$refs.backImageInput) this.$refs.backImageInput.value = '';
        },

        onProductSelect(product) {
            if (product && product.factory_id) {
                const factory = this.factories.find((fac) => fac.id === product.factory_id);
                this.formData.selectedFactoryId = factory ? factory.id : null;
            } else {
                this.formData.selectedFactoryId = null;
            }
            
            this.formData.selectedStyle = null;
            this.formData.selectedDesign = null;
            
            // Reset player sizes
            this.formData.players.forEach((player) => {
                player.size = '';
            });

            // Reset addon selections
            this.selectedAddons = [];
            this.selectedTechnology = [];
        },

        onStyleSelect(style) {
            this.formData.selectedDesign = null;

            // Initialize ungrouped addons selection
            if (style && style.customized_addons && style.customized_addons.ungrouped_addons) {
                style.customized_addons.ungrouped_addons.forEach((addon) => {
                    this.$set(addon, 'is_selected', false);
                });
            }
        },

        setSelectedAddons(e, addon, index) {            
            if (e.target.checked) {                
                this.selectedAddons.push(addon);                
            } else {
                const idx = this.selectedAddons.findIndex(a => a.id === addon.id);
                if (idx !== -1) {
                    this.selectedAddons.splice(idx, 1);
                }
            }
        },

        setLogoTechnologies(e, tech, index) {
            if (e.target.checked) {
                this.selectedTechnology.push(tech);
            } else {
                const idx = this.selectedTechnology.findIndex(t => t.sku_id === tech.sku_id);
                if (idx !== -1) {
                    this.selectedTechnology.splice(idx, 1);
                }
            }
        },

        setSelectedGroupedAddons(group, addon) {
            this.$set(this.selectedGroupAddon, group, addon);
        },

        isAddonSelected(addon) {
            return this.selectedAddons.some(a => a.id === addon.id);
        },

        isTechnologySelected(tech) {
            return this.selectedTechnology.some(t => t.sku_id === tech.sku_id);
        },

        fileChange(e, type) {
            const files = e.target.files;
            if (!files || files.length === 0) return;

            if (type === 'assets') {
                this.formData.productAssets = [...this.formData.productAssets, ...Array.from(files)];
            } else if (type === 'frontimage') {
                this.formData.frontImage = files[0];
            } else if (type === 'backimage') {
                this.formData.backImage = files[0];
            } else if (type === 'roster') {
                this.uploadExcelFile(files[0]);
            }

            e.target.value = '';
        },

        getFilePreview(file) {
            //if (!file) return placeholderImg;

            const fileExt = file.name.split('.').pop()?.toLowerCase() || '';
            if (['jpg', 'jpeg', 'png'].includes(fileExt)) {
                return URL.createObjectURL(file);
            }
            //return placeholderImg;
        },

        removeFile(index, type) {
            if (type === 'assets' && index !== null) {
                this.formData.productAssets.splice(index, 1);
            } else if (type === 'frontimage') {
                this.formData.frontImage = null;
                if (this.$refs.frontImageInput) {
                    this.$refs.frontImageInput.value = '';
                }
            } else if (type === 'backimage') {
                this.formData.backImage = null;
                if (this.$refs.backImageInput) {
                    this.$refs.backImageInput.value = '';
                }
            }
        },

        uploadExcelFile(file) {
            this.showLoader = true;
            const ext = file.name.split('.').pop();

            if (ext !== 'xlsx') {
                this.showToast('Invalid file format. Please upload an XLSX file.', 'error');
                this.showLoader = false;
                return;
            }

            readXlsxFile(file).then((rows) => {
                if (
                    rows.length < 2 ||
                    rows[0].length !== 4 ||
                    rows[0][0] !== 'NAME ON PRODUCT' ||
                    rows[0][1] !== 'NUMBER' ||
                    rows[0][2] !== 'SIZE*' ||
                    rows[0][3] !== 'QUANTITY*'
                ) {
                    this.showToast('Invalid file structure.', 'error');
                    this.showLoader = false;
                    return;
                }

                const updatedRoster = rows.slice(1).map((row) => ({
                    text: row[0] || '',
                    number: row[1] ? String(row[1]) : '',
                    size: row[2] || '',
                    quantity: row[3] || 0,
                    information: '',
                }));

                this.formData.players = updatedRoster;
                this.showLoader = false;
                this.showToast('Excel file uploaded successfully', 'SUCCESS');
            }).catch((error) => {
                console.error('Error reading Excel file:', error);
                this.showToast('Error reading Excel file', 'error');
                this.showLoader = false;
            });
        },

        setRosterData(option) {
            if (!option || !option.product_roster_detail) return;

            const rosters = option.product_roster_detail.map((product) => ({
                size: product.size || '',
                text: product.text || '',
                number: product.number || '',
                quantity: parseInt(product.quantity) || 1,
                information: product.information || '',
            }));

            this.formData.players = rosters;
        },

        addPlayer() {
            let prevPlayer = null;
            if (this.formData.players.length > 0) {
                prevPlayer = this.formData.players[this.formData.players.length - 1];
            }

            this.formData.players.push({
                text: '',
                number: '',
                size: prevPlayer ? prevPlayer.size : '',
                quantity: 1,
                information: '',
            });
        },

        removePlayer(index) {
            this.formData.players.splice(index, 1);
        },

        async downloadTemplateHandler() {
            if (!this.formData.selectedProduct || !this.companyId) return;

            const productId = this.formData.selectedProduct.id;
            const productName = this.formData.selectedProduct.display_name;

            try {
            const response = await http.get(`template/download/${productId}/${this.companyId}`, {
                responseType: 'blob',
            });
            const blob = new Blob([response.data], {
                type: response.headers['content-type'],
            });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `product_${productName}_template.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            } catch (error) {
            handleResponseException(error);
            this.showToast('Failed to download template', 'error');
            }
        },

        copyRoster() {
            const productId = this.isCustomProduct ? this.formData.customProductName : this.formData.selectedProduct?.id;
            this.$emit('copy-roster', {
                product_id: productId,
                rosterData: this.formData.players,
            });
        },

        pasteRoster() {
            if (!this.copiedRoster || !this.copiedRoster.rosterData) return;

            const rosterData = [];
            this.copiedRoster.rosterData.forEach((player) => {
                let matchedSize = '';

                if (!this.isCustomProduct && this.formData.selectedProduct) {
                    this.formData.selectedProduct.sizes[0].json_data.forEach((size) => {
                        if (size.name === player.size) {
                            matchedSize = size.name;
                        }
                    });
                } else {
                    matchedSize = player.size;
                }

                rosterData.push({
                    text: player.text,
                    number: player.number,
                    size: matchedSize,
                    quantity: player.quantity,
                    information: '',
                });
            });

            this.formData.players = rosterData;
        },

        resetForm() {
            this.formData = {
                selectedProduct: null,
                selectedStyle: null,
                selectedDesign: null,
                customProductName: null,
                customDesignName: null,
                productAssets: [],
                frontImage: null,
                backImage: null,
                selectedFactoryId: null,
                players: [
                    {
                        text: '',
                        number: '',
                        size: '',
                        quantity: 1,
                        information: '',
                    },
                ],
            };
            
            this.errors = {};
            this.playerErrors = [];
            this.selectedAddons = [];
            this.selectedTechnology = [];
            this.selectedGroupAddon = {};
            this.selectedLocker = null;
            this.selectedLockerProduct = null;

            // Reset file inputs
            if (this.$refs.assetsInput) this.$refs.assetsInput.value = '';
            if (this.$refs.frontImageInput) this.$refs.frontImageInput.value = '';
            if (this.$refs.backImageInput) this.$refs.backImageInput.value = '';
            if (this.$refs.rosterInput) this.$refs.rosterInput.value = '';
        },

        closeEvent() {
            this.resetForm();
            this.$emit('on-close');
            this.$bvModal.hide('add-product-modal');
        },
    },
});
</script>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}

.loader-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
}

.z-index-100 {
    z-index: 100;
}

.btn.active {
    background-color: #007bff;
    color: white;
    border-color: #007bff;
}

.btn.active i.fa-check {
    display: inline-block;
}

table {
    min-height: 150px !important;
}
</style>